import { IonApp, IonSpinner, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { AppReworkMigration } from "@/components/global/app-rework-migration";
import { AppUrlListener } from "@/components/global/app-url-listener";
import { CalendarSyncListener } from "@/components/global/calendar-sync-listener";
import { ErrorBoundary } from "@/components/global/error-boundary";
import { ModalProvider } from "@/components/modal/modal-provider";
import { Toaster } from "@/components/ui/sonner";
import { Capacitor } from "@/lib/capacitor";
import { PostHogPageView } from "@/lib/providers/posthog/components/PageView";
import { history } from "@/lib/router/history";
import { NotificationListener } from "./components/global/notification-listener";
import { useAuthType } from "./lib/hooks/use-auth-type";
import { useLastSettled } from "./lib/hooks/use-last-settled";
import { useProfileNudge } from "./lib/hooks/use-profile-nudge";
import { useTelemetryFlush } from "./logger";
import { GlobalRouter } from "./routes/routers/global.router";

// iOS-modus op beide platforms, zodat de app er overal hetzelfde uitziet.
//
// Dat heeft één bijwerking die je moet uitzetten: Ionic koppelt zijn eigen
// swipe-to-go-back aan die modus (`config.get("swipeBackEnabled", mode === "ios")`
// in StackManager), en dat gebaar luistert op de eerste 50 pixels vanaf de rand --
// precies waar Android zijn systeemgebaar heeft. Eén veeg werd zo twee keer terug:
// Ionic popte een pagina en Androids back-event popte er nog een.
setupIonicReact({
  mode: "ios",
  swipeBackEnabled: Capacitor.Platform !== "android",
});

export function App() {
  // Flush telemetry logs before page unload
  useTelemetryFlush();

  // Show the profile-completion nudge when an employee opens the app with an
  // incomplete profile and the interval has elapsed (#383).
  useProfileNudge();

  const { isLoading, isSignedIn, isEmployee, isAdmin } = useAuthType();

  const isUnauthenticated = !isSignedIn || (!isEmployee && !isAdmin);
  const isAuthenticated = !isUnauthenticated;

  /**
   * The boot spinner is for the boot, and only for the boot — and the answer it waits for is held
   * steady afterwards.
   *
   * `isLoading` does not stay down once it goes down. Better Auth drops `session.data` to `null`
   * whenever a `/get-session` call fails — a tunnel, a 502, a slow resume — and its next request
   * then reports `isPending` again, because it computes pending as "no data yet".
   *
   * Two things follow from that, and both used to bite. Returning the spinner branch on it unmounts
   * `IonReactRouter` and all three nested router outlets, and remounting those is exactly what
   * leaves an `ion-page` stuck at `ion-page-invisible`: a blank, tap-proof overlay over the whole
   * app. And passing the momentary answer down tells the router this employee is signed out, which
   * sends it to the sign-in screen and back a tick later — two root redirects across nested
   * outlets, which strands Ionic on a page it never finishes entering.
   *
   * So the router mounts once and stays mounted, and while auth is reloading the router keeps the
   * verdict it already had.
   */
  const auth = useLastSettled({ isAuthenticated, isAdmin }, !isLoading);

  if (!auth.hasSettled) {
    return (
      <IonApp>
        <div className="flex h-full w-full items-center justify-center">
          <IonSpinner />
        </div>
      </IonApp>
    );
  }

  return (
    <ErrorBoundary>
      <IonApp>
        <IonReactRouter history={history}>
          <ModalProvider />
          <AppUrlListener />
          <AppReworkMigration />
          <NotificationListener />
          <CalendarSyncListener />
          <PostHogPageView />
          <Toaster />

          <GlobalRouter isAuthenticated={auth.value.isAuthenticated} isAdmin={auth.value.isAdmin} />
        </IonReactRouter>
      </IonApp>
    </ErrorBoundary>
  );
}
